import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Lock, Check, Trophy } from "lucide-react";
import Nav from "../Components/Nav/Nav";
import Menu from "../Components/Nav/Menu";
import Login from "./Login";
import AcademyHero from "../Components/Shared/AcademyHero";
import { UidContext } from "../Components/App.Context";
import { getCatalog, getLeaderboard } from "../actions/formationLearner.actions";
import { groupFormationsByCategory } from "../utils/formationGroups";
import academyMascot from "../asset/formation-avatar.png";

const Formations = ({ socket }) => {
  const uid = useContext(UidContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { catalog } = useSelector((s) => s.formationLearnerReducer);
  const [query, setQuery] = useState("");
  const [me, setMe] = useState(null);

  // Cliquer une formation ouvre sa page de garde (présentation + CTA).
  const openFormation = (id) => navigate(`/formations/${id}`);

  useEffect(() => {
    const t = setTimeout(() => {
      dispatch(getCatalog({ q: query || undefined }));
    }, 250);
    return () => clearTimeout(t);
  }, [dispatch, query]);

  useEffect(() => {
    let active = true;
    dispatch(getLeaderboard())
      .then((d) => active && setMe(d && d.me))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [dispatch]);

  if (!uid) return <Login />;

  // Progression globale (formations terminées / publiées) pour l'anneau du hero
  const ringPct =
    me && me.totalPublished ? Math.min(100, Math.round((me.completed / me.totalPublished) * 100)) : 0;
  const RING_C = 2 * Math.PI * 34;

  // Regroupe le catalogue par catégorie ("Sans catégorie" en dernier)
  const groups = groupFormationsByCategory(catalog);

  const renderCard = (f) => {
    const done = f.progress && f.progress.status === "completed";
    return (
      <div
        key={f.id}
        className="formation-card"
        role="button"
        tabIndex={0}
        onClick={() => openFormation(f.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter") openFormation(f.id);
        }}
      >
        <div className="formation-cover">
          <div className={`cover-medal ${done ? "earned" : ""} variant-${f.id % 6}`}>
            <Trophy size={34} />
            <span className={`cover-medal-mark ${done ? "done" : ""}`}>
              {done ? <Check size={12} /> : <Lock size={11} />}
            </span>
          </div>
          {f.FormationCategory ? (
            <span className="formation-cat">{f.FormationCategory.name}</span>
          ) : null}
        </div>
        <div className="formation-card-body">
          <h3>{f.title}</h3>
          <div className="formation-progress">
            <span style={{ width: `${f.progress ? f.progress.percent : 0}%` }} />
          </div>
          <p className="formation-status">
            {f.progress && f.progress.status === "completed"
              ? "Terminé"
              : f.progress && f.progress.status === "suspended"
              ? "À repasser"
              : f.progress && f.progress.status === "in_progress"
              ? `${f.progress.percent}% — Continuer`
              : "Nouveau"}
          </p>
        </div>
      </div>
    );
  };

  return (
    <>
      <Nav socket={socket} />
      <div className="mainContainer">
        <Menu />
        <div className="center-Block">
          <div className="formations-panel">
          <AcademyHero
            iconSrc={academyMascot}
            title={
              <>
                <span className="sa-skills">Skills</span> Academy
              </>
            }
            tagline="Montez en compétences, validez vos quizz et décrochez vos badges."
            search={{
              value: query,
              placeholder: "Rechercher une formation…",
              onChange: (e) => setQuery(e.target.value),
            }}
            right={
              me ? (
                <div className="academy-ring" title="Progression globale">
                  <svg viewBox="0 0 80 80" className="academy-ring-svg">
                    <defs>
                      <linearGradient id="academyRingGrad" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#ffd166" />
                        <stop offset="100%" stopColor="#f7a600" />
                      </linearGradient>
                    </defs>
                    <circle className="academy-ring-track" cx="40" cy="40" r="34" />
                    <circle
                      className="academy-ring-bar"
                      cx="40"
                      cy="40"
                      r="34"
                      style={{
                        strokeDasharray: RING_C,
                        strokeDashoffset: RING_C * (1 - ringPct / 100),
                      }}
                    />
                  </svg>
                  <div className="academy-ring-label">
                    <strong>{ringPct}%</strong>
                    <span>terminé</span>
                  </div>
                </div>
              ) : null
            }
          />

          {groups.length > 0 ? (
            <div className="formations-sections">
              {groups.map((group) => (
                <section className="formations-section" key={group.key}>
                  <div className="formations-section-head">
                    <h2>{group.name}</h2>
                    <span className="formations-section-count">
                      {group.items.length} formation{group.items.length > 1 ? "s" : ""}
                    </span>
                  </div>
                  <div className="formations-grid">{group.items.map(renderCard)}</div>
                </section>
              ))}
            </div>
          ) : (
            <p className="formations-empty">Aucune formation disponible pour le moment.</p>
          )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Formations;
